/**
 * Module 'containment': what a person may ask a machine to run, and what a machine keeps out
 * of reach (node security design P0 items 11 to 16; D8, D9, D11, D18).
 *
 *   normalizePermissionMode    the four Orgus session modes; anything else is refused before
 *                              any gate, then each harness adapter maps the mode (D8)
 *   actionRoleFor              tiers bind people too: raw job.run and a session that skips
 *                              permission checks need admin (critical capabilities)
 *   reachesProtectedFolder     an action whose folder or command line reaches the Orgus home
 *                              (keys, ceiling, hub data) is refused on the machine that holds it
 *
 * The node-side ceiling names (DEFAULT_CEILING, session.start:mode:*, session.start:agent:*)
 * live with the rest of the ceiling in ./trust.ts. Browser-safe: no node imports.
 */
import type { Role } from './auth.ts';
import { BRAND_SLUGS, envNames } from './brand.ts';
import { SESSION_PERMISSION_MODES } from './scheduler.ts';
import { trimEndOf } from './trim.ts';

/** The session permission modes Orgus understands, for every harness: SESSION_PERMISSION_MODES (scheduler.ts). */
export type SessionPermissionMode = (typeof SESSION_PERMISSION_MODES)[number];

/**
 * A permission mode as given, checked: undefined when none was given (the harness default),
 * the mode when it is one of the four, null for anything else (a raw harness value such as
 * 'danger-full-access', or text that could read as an option). Callers refuse null.
 */
export function normalizePermissionMode(v: unknown): SessionPermissionMode | null | undefined {
  if (v === undefined || v === null || v === '') return undefined;
  if (typeof v !== 'string') return null;
  return (SESSION_PERMISSION_MODES as readonly string[]).includes(v) ? (v as SessionPermissionMode) : null;
}

/**
 * The mode a session.start asks for, for gates and ceilings. A value that is not one of the
 * four counts as bypassPermissions, so no gate can be dodged by naming a mode it does not list
 * (the Go agent's SessionModeOf does the same).
 */
export function sessionModeOf(params: Record<string, unknown> | null | undefined): SessionPermissionMode {
  const m = normalizePermissionMode(params?.permissionMode);
  if (m === undefined) return 'default';
  return m ?? 'bypassPermissions';
}

/**
 * F3 (review C3): a session.start's params as every gate and the launch read them. `mode` and
 * `agentKind` are trimmed and lower-cased, `permissionMode` and `model` trimmed, so a value such
 * as "terminal " or "Codex" cannot pass a ceiling that names it and then start anyway once the
 * handler trims it. The agent normalises once and hands the same object to its ceiling and to
 * the action; ceilingHit normalises too, for the hub's own pre-checks. A permission mode in the
 * wrong case stays unknown (normalizePermissionMode), so it still counts as bypassPermissions.
 */
export function normalizeSessionStartParams(params: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...params };
  for (const k of ['mode', 'agentKind']) if (typeof out[k] === 'string') out[k] = (out[k] as string).trim().toLowerCase();
  for (const k of ['permissionMode', 'model']) if (typeof out[k] === 'string') out[k] = (out[k] as string).trim();
  return out;
}

/** An action's params, normalised where a gate reads them (session.start); anything else as it is. */
export function normalizeActionParams(kind: string, params: unknown): unknown {
  if (kind !== 'session.start' || !params || typeof params !== 'object' || Array.isArray(params)) return params;
  return normalizeSessionStartParams(params as Record<string, unknown>);
}

/** Why a permission mode is refused, in words a person can act on. */
export const PERMISSION_MODE_REFUSAL = `That permission mode is not one Orgus knows. Use one of: ${SESSION_PERMISSION_MODES.join(', ')}.`;

/**
 * Critical actions: as much as a shell on the machine. Raw job.run runs any command line; a
 * session with permission checks off runs any tool without asking.
 */
export function isCriticalAction(kind: string, params: Record<string, unknown> | null | undefined = {}): boolean {
  if (kind === 'job.run') return true;
  return kind === 'session.start' && sessionModeOf(params) === 'bypassPermissions';
}

/**
 * The least role a person needs to send this action to a machine (D8: tiers apply to people,
 * not only to AI agents). Critical actions need admin; everything else operator. The hub's own
 * machine raises critical actions to owner (HUB_MACHINE_CRITICAL_ROLE).
 */
export function actionRoleFor(kind: string, params: Record<string, unknown> | null | undefined = {}): Role {
  return isCriticalAction(kind, params) ? 'admin' : 'operator';
}

/** Who may send a critical action to the computer the hub itself runs on (D18). */
export const HUB_MACHINE_CRITICAL_ROLE: Role = 'owner';

// ---------------------------------------------------------------------------
// What the floor reads at most
// ---------------------------------------------------------------------------

/**
 * The longest command line, path or session folder the floor reads, in UTF-16 code units (as
 * JavaScript counts a string; the Go agent counts the same way). Anything longer is refused
 * outright, before any parsing, never cut and checked (containment review F4): judging 1 MB
 * took 6.9 s in agent-ts and 8.3 s in the Go agent, stalling every hook on the machine.
 *
 * Why 32,768: Windows starts no process whose command line is longer than 32,767 UTF-16 units
 * (CreateProcess), and no path is longer either, so nothing that runs there is refused; it is
 * the same bound the real-path check uses for paths (agent-ts real-floor.ts, Go realfloor).
 * A POSIX shell takes more (128 KiB per word on Linux), but a model's command line that long
 * is file contents in a heredoc, which a file tool writes instead. At this size the slowest
 * line measured (a|a|a|..., 16,000 parts) takes about 0.2 s, where 1 MB took seconds.
 * The Go agent's policyeval.FloorTextMax is the same number (a parity vector).
 */
export const FLOOR_TEXT_MAX = 32_768;

/** Is this text longer than the floor reads (FLOOR_TEXT_MAX)? Non-strings are not text: false. */
export function floorTextTooLong(s: unknown): boolean {
  return typeof s === 'string' && s.length > FLOOR_TEXT_MAX;
}

/** Why the floor refuses a command line or path it will not read. */
export const FLOOR_TOO_LONG_REASON =
  'The command or path is too long for the floor to check (over 32,768 characters), so it is refused. Split it into shorter commands, or write long content with a file tool.';

// ---------------------------------------------------------------------------
// Protected folders
// ---------------------------------------------------------------------------

/** A path compared as text: forward slashes, no trailing slash, lower case (Windows and macOS fold case). */
export function foldPath(p: string): string {
  let s = p.trim().replace(/\\/g, '/').replace(/\/{2,}/g, '/');
  // Git Bash style /c/Users/... is c:/Users/...
  const msys = /^\/([a-zA-Z])(\/.*)?$/.exec(s);
  if (msys) s = `${msys[1]}:${msys[2] ?? '/'}`;
  if (s.length > 1 && s.endsWith('/') && !/^[A-Za-z]:\/$/.test(s)) s = trimEndOf(s, '/');
  return s.toLowerCase();
}

/** Is `p` the folder `dir` or inside it? Both absolute; `.` and `..` segments are resolved. */
export function pathInside(p: string, dir: string): boolean {
  const collapse = (x: string): string => {
    const out: string[] = [];
    for (const seg of x.split('/')) {
      if (seg === '.' || (seg === '' && out.length > 0)) continue;
      if (seg === '..') {
        if (out.length > 1) out.pop();
        continue;
      }
      out.push(seg);
    }
    return out.join('/') || '/';
  };
  const a = collapse(foldPath(p));
  const b = collapse(foldPath(dir));
  return a === b || a.startsWith(b.endsWith('/') ? b : `${b}/`);
}

/**
 * The Orgus home's name as it may appear in a command line: the environment variables that
 * point at it, and the default folder names (~/.orgus and earlier names).
 */
export function protectedHomeMarkers(): string[] {
  return [...envNames('HOME').map((n) => n.toLowerCase()), ...BRAND_SLUGS.map((s) => `/.${s}`)];
}

/**
 * Which protected folder this action reaches, or null. `cwd` is resolved by the caller (an
 * absolute path): inside a protected folder or above one counts (RT-1), and it is the folder a
 * relative path in the command line starts from (commandReachesProtectedFolder).
 */
export function reachesProtectedFolder(o: { cwd?: string | null; command?: string | null }, folders: readonly string[]): string | null {
  const dirs = protectedDirs(folders);
  if (o.cwd) {
    // Inside the folder, or a folder that contains it (a session in the user profile reaches
    // ~/.orgus as "its own folder"): either way the keys are in reach (RT-1).
    for (const d of dirs) if (pathInside(o.cwd, d) || pathInside(d, o.cwd)) return d;
  }
  return o.command ? commandReachesProtectedFolder(o.command, dirs, o.cwd) : null;
}

// What counts as reaching the Orgus home from text. The protected folders themselves (the
// resolved ORGUS_HOME and ~/.orgus) count wherever they appear, and so do the home variables
// ($ORGUS_HOME, %ORGUS_HOME%, ...). A folder named .orgus (or an earlier name) counts unless the
// text shows it lies in a folder that is plainly not a home: an absolute path at least two
// folders deep, not /root, /home/<u>, /Users/<u>, C:/Users/<u> or a Windows system folder, with
// nothing a shell expands (~, $, %, globs, ...) and no '..'; or a relative path, when the folder
// it starts from is known (the session's cwd, and any cd to such an absolute path). Either way
// only when the line has no sign of a home elsewhere (~, $HOME, %USERPROFILE%, homedir(),
// Join-Path, '+', a word such as /home/bob or C:/Users/bob, a '..'), which could copy, link or
// join a home to it. A repo's own config folder (<repo>/.orgus/layout.json) is ordinary work;
// ~/.orgus, cd ~ && cat .orgus/x and C:/Users/<u>/.orgus are not. When in doubt, it counts.

/** Characters a shell or a program expands: a path holding one names a folder its text does not show. */
const EXPANDS = /[~$%`*?[\]{}!^]/;
/** Signs in a command line of a home folder named elsewhere, which a path may be joined to. */
const HOME_WORDS = /[~$%`+]|home *\(|\.home|homedir|home_dir|homepath|homedrive|userprofile|expanduser|environ|getenv|env\[|env\.get|process\.env|getfolderpath|specialfolder|join-path|\.join *\(|combine *\(|concat *\(/;
/** A relative path plain enough to resolve against a known folder. */
const PLAIN_RELATIVE = /^[a-z0-9._@ /-]*$/;
/** Folder names whose children are home folders, and folders that are a home by name. */
const HOME_PARENTS = new Set(['home', 'users', 'documents and settings']);
const HOME_NAMES = new Set(['home', 'users', 'root', 'documents and settings']);
/** Commands whose first operand is the folder they move to. */
const CD_COMMANDS = new Set(['cd', 'chdir', 'pushd', 'sl', 'set-location', 'push-location']);
/** Words that move to a folder (or a user, and so a home) the text does not show. */
const CD_UNKNOWN = new Set(['popd', 'pop-location', 'sudo', 'su', 'doas', 'runas', 'runuser', 'pkexec', 'ssh', 'wsl', 'machinectl', 'login', '-execdir', '-okdir']);
/** Options that name the folder a program works in (git -C is matched as typed, case and all). */
const CD_OPTION = /^--?(cwd|dir|directory|prefix|chdir|cd|workdir|work-dir|working-directory|workingdirectory|work-tree|git-dir|wd)(=|$)/;
const NAME_CHAR = /[a-z0-9_-]/;

/** An absolute folder: its drive ('' or 'c:') and its lower-case segments, '.' dropped. */
interface Folder {
  root: string;
  segs: string[];
}

function protectedDirs(folders: readonly string[]): string[] {
  return folders.filter((d) => typeof d === 'string' && d.trim() !== '');
}

/** An absolute path's folder, or null when it is not absolute, steps up with '..' or holds a ':' after the drive. */
function absFolder(p: string): Folder | null {
  const m = /^([a-z]:)?\/(.*)$/.exec(foldPath(p));
  if (!m) return null;
  const segs = (m[2] ?? '').split('/').filter((x) => x !== '' && x !== '.');
  if (segs.some((x) => x === '..' || x.includes(':'))) return null;
  return { root: m[1] ?? '', segs };
}

const folderText = (f: Folder): string => `${f.root}/${f.segs.join('/')}`;

/**
 * A folder name as Windows reads it: trailing dots and blanks dropped (C:/Users./bob is
 * C:/Users/bob). A loop, not /[. ]+$/, which V8 runs in quadratic time (review F3).
 */
const winName = (s: string | undefined): string => trimEndOf(s ?? '', '. ');

/** A home by its shape: /home, /home/<u>, /Users/<u>, /root, C:/Users/<u>. */
function isHomeFolder(f: Folder): boolean {
  const n = f.segs.length;
  return n >= 1 && (HOME_NAMES.has(winName(f.segs[n - 1])) || (n >= 2 && HOME_PARENTS.has(winName(f.segs[n - 2]))));
}

/** A home, or a folder that may be one: too shallow, home-shaped, or C:/Windows/... (service profiles). */
function homeLike(f: Folder): boolean {
  return f.segs.length < 2 || isHomeFolder(f) || winName(f.segs[0]) === 'windows';
}

/** A word that names a home itself (cp -r /home/bob /tmp/x) or steps up with '..' (ln -s .. h): what it reaches is not plain. */
function homeSign(word: string): boolean {
  const p = word.slice(Math.max(word.lastIndexOf('='), word.lastIndexOf(',')) + 1);
  if (p.split('/').includes('..')) return true;
  const f = absFolder(p);
  return f !== null && isHomeFolder(f);
}

const samePath = (a: string, b: string): boolean => pathInside(a, b) && pathInside(b, a);
const parentOf = (d: string): string => foldPath(d).replace(/\/[^/]*$/, '') || '/';

/** How a path in some text is judged: the folders a relative path may start from (null: unknown). */
interface Scope {
  bases: Folder[] | null;
  /** The text points at a home somewhere ($HOME, ~, homedir(), /home/bob, '..'): every '.orgus' counts. */
  homeSigns: boolean;
  /** A path may start after '=', ',' or ':' inside a word (command lines; not a bare path). */
  delims: boolean;
}

/** Does this '.<slug>' folder, at index i of the lower-case word t, reach an Orgus home? */
function judgeHomeName(t: string, i: number, slug: string, dirs: readonly string[], scope: Scope): string | null {
  const name = `.${slug}`;
  const before = t.slice(0, i);
  // A home named elsewhere on the line may be copied, linked or joined to this path.
  if (scope.homeSigns || EXPANDS.test(before)) return name;
  let p = before;
  if (scope.delims) {
    p = p.slice(Math.max(p.lastIndexOf('='), p.lastIndexOf(',')) + 1);
    if (!/^[a-z]:\//.test(p) && p.includes(':')) {
      if (/^[a-z]:/.test(p)) return name; // c:.orgus: wherever drive C stands
      p = p.slice(p.lastIndexOf(':') + 1); // HEAD:.orgus, host:.orgus, -Path:C:/x
    }
  }
  let parents: Folder[];
  if (p.startsWith('/') || /^[a-z]:\//.test(p)) {
    const f = absFolder(p);
    if (!f) return name;
    parents = [f];
  } else {
    if (!scope.bases?.length || !PLAIN_RELATIVE.test(p)) return name;
    const rel = p.split('/').filter((x) => x !== '' && x !== '.');
    if (rel.includes('..')) return name;
    parents = scope.bases.map((b) => ({ root: b.root, segs: [...b.segs, ...rel] }));
  }
  for (const f of parents) {
    if (homeLike(f)) return name;
    const dir = folderText(f);
    for (const d of dirs) if (pathInside(`${dir}/${name}`, d) || pathInside(d, `${dir}/${name}`)) return d;
    if (dirs.some((d) => samePath(dir, parentOf(d)))) return name;
  }
  return null;
}

/**
 * How many '.<slug>' folder names one word may hold before it counts without judging the rest.
 * Each is judged from the start of the word, so a word of thousands (.orgus./.orgus./...) took
 * seconds; no real path names the home's folder name this often (containment review F3).
 */
export const HOME_NAMES_JUDGED = 8;

/** The first '.<slug>' folder in the lower-case word t that reaches an Orgus home. */
function scanHomeNames(t: string, dirs: readonly string[], scope: Scope): string | null {
  let judged = 0;
  for (const slug of BRAND_SLUGS) {
    const name = `.${slug}`;
    for (let i = t.indexOf(name); i >= 0; i = t.indexOf(name, i + 1)) {
      const prev = i > 0 ? t[i - 1]! : '';
      const next = t[i + name.length] ?? '';
      if ((prev && (prev === '.' || NAME_CHAR.test(prev))) || (next && NAME_CHAR.test(next))) continue; // x.orgus, .orgus-docs
      if (++judged > HOME_NAMES_JUDGED) return name;
      const hit = judgeHomeName(t, i, slug, dirs, scope);
      if (hit) return hit;
    }
  }
  return null;
}

/** A command line's words as a shell passes them: quotes removed, split at blanks and ; | & ( ) < > ` outside quotes. */
function shellWords(command: string): string[] {
  const out: string[] = [];
  let cur = '';
  let quote = '';
  for (const ch of command) {
    if (quote) {
      if (ch === quote) quote = '';
      else cur += ch;
    } else if (ch === '"' || ch === "'") quote = ch;
    else if (' \t\n\r\v\f;|&()<>`'.includes(ch)) {
      if (cur) out.push(cur);
      cur = '';
    } else cur += ch;
  }
  if (cur) out.push(cur);
  return out;
}

/** The folders a command line moves to (cd, pushd, git -C, --cwd, ...), or null when one is not plainly a non-home absolute folder. */
function folderChanges(words: readonly string[]): Folder[] | null {
  const out: Folder[] = [];
  for (let i = 0; i < words.length; i++) {
    const w = words[i]!;
    const l = w.toLowerCase();
    if (CD_UNKNOWN.has(l) || /^cd[./]/.test(l) || (l !== 'chdir' && l.endsWith('chdir')) || l.includes('currentdirectory')) return null;
    let target: string | undefined;
    if (CD_COMMANDS.has(l)) {
      let j = i + 1;
      while (j < words.length && (words[j]!.startsWith('-') || words[j]!.toLowerCase() === '/d')) j++;
      target = words[j];
    } else if (w.startsWith('-C')) target = w.length > 2 ? w.slice(2) : words[i + 1];
    else {
      const m = CD_OPTION.exec(l);
      if (!m) continue;
      target = m[2] === '=' ? w.slice(w.indexOf('=') + 1) : words[i + 1];
    }
    const f = target === undefined || EXPANDS.test(target) ? null : absFolder(target);
    if (!f || homeLike(f)) return null;
    out.push(f);
  }
  return out;
}

/** The session's own folder as a starting point for relative paths, or null. */
function cwdFolder(cwd: string | null | undefined): Folder | null {
  return cwd ? absFolder(cwd) : null;
}

/** A protected folder that a path in the lower-case word t lies in (any folder name), or null. */
function wordInFolder(t: string, dirs: readonly string[], bases: readonly Folder[], delims: boolean): string | null {
  const p = delims ? t.slice(Math.max(t.lastIndexOf('='), t.lastIndexOf(',')) + 1) : t;
  if (!p || EXPANDS.test(p)) return null;
  const cands = p.startsWith('/') || /^[a-z]:\//.test(p) ? [p] : bases.map((b) => `${folderText(b)}/${p}`);
  for (const c of cands) for (const d of dirs) if (pathInside(c, d)) return d;
  return null;
}

const VAR_PREFIXES = ['$', '${', '$env:', '%'];

/**
 * A home variable read as a value: $NAME, ${NAME}, $env:NAME or %NAME%. Naming it alone, as in
 * NAME=<a test folder> for a child process (the repo's own test setup), reaches nothing; a value
 * that is the real home is still caught by the folder and default-name checks.
 */
function refersTo(text: string, name: string): boolean {
  for (const p of VAR_PREFIXES) {
    for (let at = text.indexOf(p + name); at >= 0; at = text.indexOf(p + name, at + 1)) {
      if (!/[a-z0-9_]/.test(text.charAt(at + p.length + name.length))) return true;
    }
  }
  return false;
}

/** A Windows short name for a home folder: C:/Users/x/ORGUS~1/agent.json. */
const SHORT_NAMES = BRAND_SLUGS.map((s) => ({ name: `.${s}`, re: new RegExp(`(^|[^a-z0-9_.-])${s.slice(0, 6)}~[0-9]+/`) }));

function shortHomeName(lower: string): string | null {
  for (const s of SHORT_NAMES) if (s.re.test(lower)) return s.name;
  return null;
}

/**
 * Which protected folder a command line reaches, or null. `cwd` is where it runs (absolute),
 * the start of its relative paths; without it a relative '.orgus' counts. A cd somewhere the
 * text does not show (cd ~, cd .., popd, sudo, ssh, ...) makes a relative '.orgus' count too.
 */
export function commandReachesProtectedFolder(command: string, folders: readonly string[], cwd?: string | null): string | null {
  const dirs = protectedDirs(folders);
  const slashed = command.replace(/\\/g, '/');
  const text = ` ${slashed.toLowerCase()} `;
  for (const d of dirs) if (text.includes(foldPath(d))) return d;
  for (const n of envNames('HOME')) if (refersTo(text, n.toLowerCase())) return n;
  const short = shortHomeName(text);
  if (short) return short;
  const words = shellWords(slashed);
  const changes = folderChanges(words);
  const here = cwdFolder(cwd);
  // An escaped blank (bob\ smith) splits differently in a shell than here: relative paths count.
  const bases = here && changes && !/\\[ \t]/.test(command) ? [here, ...changes] : null;
  const lower = words.map((w) => w.toLowerCase());
  const scope: Scope = { bases, homeSigns: HOME_WORDS.test(text) || lower.some(homeSign), delims: true };
  const starts = [...(here ? [here] : []), ...(changes ?? [])];
  for (const t of lower) {
    const hit = wordInFolder(t, dirs, starts, true) ?? scanHomeNames(t, dirs, scope);
    if (hit) return hit;
  }
  // A backslash a POSIX shell drops (~/.or\gus) still names the home.
  for (const w of shellWords(command.replace(/\\/g, ''))) {
    const hit = scanHomeNames(w.toLowerCase(), dirs, scope);
    if (hit) return hit;
  }
  return null;
}

/**
 * Which protected folder one path reaches (a file a tool reads or writes), or null. A relative
 * path starts at `cwd`; the same names count as in a command line, read as one literal path.
 */
export function pathReachesProtectedFolder(path: string, folders: readonly string[], cwd?: string | null): string | null {
  const dirs = protectedDirs(folders);
  const t = path.trim().replace(/\\/g, '/').toLowerCase();
  if (!t) return null;
  const short = shortHomeName(` ${t}`);
  if (short) return short;
  const here = cwdFolder(cwd);
  return wordInFolder(t, dirs, here ? [here] : [], false) ?? scanHomeNames(t, dirs, { bases: here ? [here] : null, homeSigns: false, delims: false });
}
